import { DayObject } from "@/types/date"

import { baseApi } from "./base-api"

/** Who a saved traveler is to the account holder. The account has at most one self. */
export type TravelerRelationship = "self" | "spouse" | "child" | "other"

export const TRAVELER_FIELD_KEYS = [
  "first_name",
  "middle_name",
  "last_name",
  "email_address",
  "phone_number",
  "date_of_birth",
  "gender",
  "country_code",
  "known_traveler_number",
  "redress_number",
  "address_line1",
  "address_line2",
  "city",
  "state",
  "postal_code",
  "address_country_code",
] as const

export type TravelerFieldKey = (typeof TRAVELER_FIELD_KEYS)[number]

/** A date of birth is a DayObject; every other value is a string. */
export type TravelerFieldValue = string | DayObject

export interface TravelerField {
  /** A traveler field, or loyalty.<key> for a member number. */
  key: string
  reference: string
  sensitivity: "contact" | "identity" | "loyalty"
  revision: number
  confirmed: boolean
  proposed: boolean
  proposal_id?: string
  value?: TravelerFieldValue | null
  proposal?: TravelerFieldValue | null
}

export interface TravelerLoyalty {
  /** Null for a program name no catalog entry matches. */
  program_key: string | null
  program: string
  kind: "airline" | "hotel" | "credit_card" | "misc" | "ota"
  tier: string | null
  points_balance: number | null
  certificates: {
    description: string
    quantity: number | null
    expires_on: DayObject | null
  }[]
  member_number_last4: string | null
  masked_member_number: string | null
  number_state: "confirmed" | "proposed" | null
  proposed_number_last4: string | null
  number_reference: string | null
}

export interface SavedTraveler {
  id: string
  relationship: TravelerRelationship
  loyalty: TravelerLoyalty[]
  fields: TravelerField[]
}

export interface BrowserPrivateProfile {
  travelers: SavedTraveler[]
  site_accounts: { domain: string; name: string }[]
}

export type BrowserProfileUpdate =
  | { action: "create"; relationship: TravelerRelationship }
  | { action: "delete_traveler"; traveler_id: string }
  | {
      action: "save"
      traveler_id: string
      field: string
      revision: number
      value: string
      confirmed: true
    }
  | {
      action: "confirm"
      traveler_id: string
      field: string
      revision: number
      proposal_id: string
      confirmed: true
    }
  | {
      action: "delete_field"
      traveler_id: string
      field: string
      revision: number
    }

export interface TravelerLoyaltyChange {
  program_key: string
  tier?: string | null
  points_balance?: string | null
  /** expires_on is YYYY-MM-DD. */
  certificates?:
    | {
        description: string
        quantity: number | null
        expires_on: string | null
      }[]
    | null
  remove?: true
}

export interface TravelerFieldsUpdate {
  travelerId: string
  /** Each field with the revision it was read at; a null or empty value deletes it. */
  fields?: { field: string; value: string | null; revision: number }[]
  loyalty?: TravelerLoyaltyChange[]
}

export const browserProfileApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Private values for the settings editor only. Never forward them to an agent.
    getSavedTravelers: builder.query<BrowserPrivateProfile, void>({
      query: () => ({
        url: "/api/browser_profile",
        params: { private_values: true },
      }),
      keepUnusedDataFor: 0,
    }),
    updateSavedTravelers: builder.mutation<
      BrowserPrivateProfile,
      BrowserProfileUpdate
    >({
      query: (profile) => ({
        url: "/api/browser_profile",
        method: "POST",
        data: { profile },
      }),
      async onQueryStarted(_update, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled
        void dispatch(
          browserProfileApi.util.upsertQueryData(
            "getSavedTravelers",
            undefined,
            data
          )
        )
      },
    }),
    // All or nothing: a stale revision saves nothing.
    updateSavedTravelerFields: builder.mutation<
      SavedTraveler,
      TravelerFieldsUpdate
    >({
      query: ({ travelerId, fields, loyalty }) => ({
        url: `/api/browser_profile/travelers/${encodeURIComponent(travelerId)}/fields`,
        method: "PATCH",
        data: {
          ...(fields?.length ? { fields } : {}),
          ...(loyalty?.length ? { loyalty } : {}),
        },
      }),
      async onQueryStarted(_update, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled
        dispatch(
          browserProfileApi.util.updateQueryData(
            "getSavedTravelers",
            undefined,
            (profile) => {
              const index = profile.travelers.findIndex(
                (traveler) => traveler.id === data.id
              )
              if (index >= 0) profile.travelers[index] = data
            }
          )
        )
      },
    }),
  }),
})

export const {
  useGetSavedTravelersQuery,
  useUpdateSavedTravelersMutation,
  useUpdateSavedTravelerFieldsMutation,
} = browserProfileApi
